/** True if `advertised` matches `query` exactly, or is a dotted prefix of it. */
function skillMatches(advertised, query, allowPrefix) {
    if (advertised === query)
        return true;
    if (!allowPrefix)
        return false;
    return query.startsWith(advertised + '.');
}
/**
 * Find peers that advertise a given skill. `skills[]` (the existing
 * cap field) is treated as the authoritative-leaf list; the Layer-4
 * `authoritative[]` field — when present — is merged in (a peer might
 * have set one and not the other; both are honored).
 */
export function findPeersBySkill(roster, skill, opts = {}) {
    const availableOnly = opts.availableOnly ?? true;
    const allowPrefix = opts.matchPrefixes ?? false;
    return roster.filter((p) => {
        if (availableOnly && !p.available)
            return false;
        if (opts.excludePeerId && p.peerId === opts.excludePeerId)
            return false;
        const authoritative = [
            ...(p.skills ?? []),
            ...(p.authoritative ?? []),
        ];
        return authoritative.some((s) => skillMatches(s, skill, allowPrefix));
    });
}
/**
 * Find peers that have a named tool in their cap.tools[].
 *
 * Tools are matched by exact name. Mesh-namespaced names (e.g.
 * `mcp:tandem.ac/search`) are matched the same way — pass the full
 * namespaced name in.
 */
export function findPeersByTool(roster, toolName, opts = {}) {
    const availableOnly = opts.availableOnly ?? true;
    return roster.filter((p) => {
        if (availableOnly && !p.available)
            return false;
        if (opts.excludePeerId && p.peerId === opts.excludePeerId)
            return false;
        return p.tools.some((t) => t.name === toolName);
    });
}
/**
 * Find peers running a model in the given codec-maps family.
 * Family is matched by mapping `cap.modelId` through the same regex
 * table mesh-react's `useCodecMapResolver` uses; for portability we
 * accept the family id directly (e.g. `qwen/qwen2`) AND a coarser
 * substring match against the modelId for ad-hoc cases.
 */
export function findPeersByModelFamily(roster, family, opts = {}) {
    const availableOnly = opts.availableOnly ?? true;
    const fam = family.toLowerCase();
    return roster.filter((p) => {
        if (availableOnly && !p.available)
            return false;
        if (opts.excludePeerId && p.peerId === opts.excludePeerId)
            return false;
        const mid = p.modelId.toLowerCase();
        // Direct family-id match via the modelId substring (e.g.
        // `qwen/qwen2` ↔ `Qwen2.5-0.5B-Instruct-q4f16_1-MLC`):
        const famTail = fam.includes('/') ? fam.split('/').pop() : fam;
        return mid.includes(famTail);
    });
}
/**
 * Find peers advertising a delegating zone that's a prefix of `skill`.
 * Used by the Layer-4 DNS-style resolver: when no peer is authoritative,
 * walk to a delegator. Sorted by zone length descending (longest-prefix
 * wins, exactly like DNS).
 */
export function findDelegatingPeers(roster, skill, opts = {}) {
    const availableOnly = opts.availableOnly ?? true;
    const matches = [];
    for (const p of roster) {
        if (availableOnly && !p.available)
            continue;
        if (opts.excludePeerId && p.peerId === opts.excludePeerId)
            continue;
        const delegating = p
            .delegating ?? [];
        for (const zone of delegating) {
            if (skill === zone || skill.startsWith(zone + '.')) {
                matches.push({ peer: p, zoneLength: zone.length });
                break; // one match per peer is enough
            }
        }
    }
    matches.sort((a, b) => b.zoneLength - a.zoneLength);
    return matches.map((m) => m.peer);
}
/**
 * Pick the "best" peer out of a candidate list. Default scoring:
 * freshest `lastSeen` wins (a peer that re-announced their cap
 * recently is more likely to still be online). Returns null on
 * empty input.
 *
 * Pass `scoring` for custom strategies (e.g. prefer specific models,
 * prefer specific nicks, prefer specific networks).
 */
export function pickBestPeer(candidates, scoring) {
    if (candidates.length === 0)
        return null;
    const score = scoring ?? ((p) => p.lastSeen);
    let best = candidates[0];
    let bestScore = score(best);
    for (let i = 1; i < candidates.length; i++) {
        const c = candidates[i];
        const s = score(c);
        if (s > bestScore) {
            best = c;
            bestScore = s;
        }
    }
    return best;
}
